<template>
  <div class="bar-chart" v-if="hasData">
    <svg ref="chart" @touchmove="touchmoveFn" @mouseleave="mouseleaveFn">
      <g class="line-rect">
        <line :x1="LEFT_PADDING" y1="0" :x2="LEFT_PADDING" :y2="chartHeight" />
        <line :x1="LEFT_PADDING" :y1="chartHeight - 0.5" :x2="chartWidth" :y2="chartHeight - 0.5" />
      </g>
      <g class="grid">
        <g v-for="grid in grids" :key="`grid-${grid.y}`">
          <text :x="LEFT_PADDING - 5" :y="grid.y + 3" text-anchor="end">{{grid.label}}</text>
          <line :x1="LEFT_PADDING" :y1="grid.y" :x2="chartWidth" :y2="grid.y" stroke-width="1" />
        </g>
      </g>
      <g class="select">
        <line v-if="currentDot" :x1="0" y1="0" :x2="0" :y2="chartHeight" :style="{transform: `translateX(${currentDot.x}px)`}" stroke="#000" />
      </g>
      <clipPath id="data-clip-path">
        <rect :x="LEFT_PADDING" y="0" :width="chartWidth" :height="chartHeight" fill="#fff" />
      </clipPath>
      <g clip-path="url(#data-clip-path)" class="data">
        <path :d="pathD" fill="transparent" />
        <circle :cx="dot.x" :cy="dot.y" :key="`dot-${dot.x}`" r="2" v-for="dot in dots" />
      </g>
    </svg>
    <div
      class="tooltip-container"
      :style="{left: `${currentDot.x + 10}px`}"
      :class="{'is-left': currentDot.x > chartWidth * 0.5}"
      v-if="currentDot"
    >
      <Card class="tooltip">
        <div class="tooltip-date"><i class="fas fa-child"></i>{{ currentDot.ageStr }}</div>
        <div class="tooltip-date"><i class="fas fa-calendar"></i>{{ currentDot.timestamp | formatTimestamp }}</div>
        <div class="tooltip-value">{{ currentDot.value | round }}{{valueUnit}}</div>
      </Card>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import Card from '@/components/atoms/Card'

const getDotPosition = (min, max, val, maxPx, startPadding) => {
  if (min === max) return (startPadding + maxPx) / 2
  return (val - min) / (max - min) * (maxPx - startPadding) + startPadding
}

const LEFT_PADDING = 25
const GRID_SPAN_LIST = [0.01, 0.025, 0.05, 0.1, 0.25, 0.5, 1, 2.5, 5, 10, 25, 50, 100, 250, 500, 1000, 2500, 5000]

export default {
  name: 'LineChart',
  components: {
    Card,
  },
  data() {
    return {
      chartWidth: 1,
      chartHeight: 1,
      LEFT_PADDING,
      mouseOffsetX: -1,
    }
  },
  filters: {
    formatTimestamp(timestamp) {
      return timestamp.format('YYYY/MM/DD HH:mm')
    },
    round(value) {
      const digit = 2
      const digitTimes = Math.pow(10, digit)
      return Math.round(value * digitTimes) / digitTimes
    },
  },
  props: {
    chartData: {
      type: Array,
      default: null,
    },
    chartFrom: {
      type: moment.Moment,
    },
    chartTo: {
      type: moment.Moment,
    },
    valueUnit: {
      type: String,
      default: '',
    },
  },
  watch: {
    currentDot(currentDot) {
      this.$emit('changeSelect', currentDot)
    },
  },
  computed: {
    hasData() {
      return this.chartData.length >= 2
    },
    currentDataIndex() {
      if (this.mouseOffsetX < 0) return -1
      const distancs = this.dots.map(d => Math.abs(d.x - this.mouseOffsetX))
      return distancs.indexOf(Math.min.apply(null,distancs))
    },
    currentDot() {
      const dot = this.dots[this.currentDataIndex]
      const hasShowableDot = dot => dot &&  dot.x > this.LEFT_PADDING && dot.x < this.chartWidth
      return hasShowableDot(dot) ? dot : null
    },
    innerChartData() {
      return this.chartData.sort((a,b)=> a > b ? 1 : -1)
    },
    maxVal() {
      return Math.max(...this.innerChartData.map(d => d.value))
    },
    minVal() {
      return Math.min(...this.innerChartData.map(d => d.value))
    },
    chartFromX() {
      return Number(this.chartFrom.format('X'))
    },
    chartToX() {
      return Number(this.chartTo.format('X'))
    },
    dots() {
      return this.innerChartData.map((d, index) => ({
        index,
        x: this.getX(d.timestamp),
        y: this.getY(d.value),
        timestamp: d.timestamp,
        value: d.value,
        ageStr: d.ageStr,
      }))
    },
    pathD() {
      return this.dots.map((d, i) => `${i === 0 ? 'M' : 'L'} ${d.x} ${d.y}`).join(' ')
    },
    grids() {
      const valRange = this.maxVal - this.minVal
      const span = GRID_SPAN_LIST.find(n => valRange / n < 10) || 1000

      let gridVal = this.minVal - (this.minVal % span)
      const grids = []
      while (gridVal < this.maxVal) {
        grids.push({ y: this.getY(gridVal), label: gridVal })
        gridVal += span
      }
      return grids
    },
  },
  methods: {
    refleshChartSize() {
      const $chart = this.$refs.chart
      if (!$chart) return
      this.chartWidth = $chart.clientWidth
      this.chartHeight = $chart.clientHeight
    },
    getX(valueTime) {
      return getDotPosition(
        this.chartFromX,
        this.chartToX,
        Number(valueTime.format('X')),
        this.chartWidth,
        LEFT_PADDING,
      )
    },
    getY(valueNum) {
      return getDotPosition(
        this.maxVal,
        this.minVal,
        valueNum,
        this.chartHeight,
        0,
      )
    },
    touchmoveFn(e) {
      const clientRect = e.srcElement.getBoundingClientRect()
      this.mouseOffsetX = e.touches[0].clientX - clientRect.x
      e.preventDefault()
    },
    mouseleaveFn(e) {
      this.mouseOffsetX = -1
      e.preventDefault()
    },
  },
  mounted() {
    this.$nextTick(() => this.refleshChartSize())
    window.addEventListener('resize', () => this.refleshChartSize())
  },
}
</script>

<style scoped lang="scss">
.bar-chart {
  position: relative;
}

svg {
  width: 100%;
  height: 200px;
  vertical-align: middle;

  path {
    stroke: $primary;
    stroke-linecap: round;
    stroke-width: 1.5px;
  }
  circle {
    fill: $primary;
  }
}

.line-rect {
  line {
    stroke: $light;
    stroke-linecap: round;
    stroke-width: 1px;
  }
}
.grid {
  line {
    stroke: $background;
  }
  text {
    font-size: 8px;
    color: $base;
  }
}

.select {
  line {
    transition: all 0.2s ease-out;
    stroke: rgba($secondary, 0.25);
  }
}

.tooltip {
  padding: 10px;
}
.tooltip-container {
  position: absolute;
  top: 10px;
  width: 120px;
  transition: all 0.2s ease-out;
  pointer-events: none;
  &.is-left {
    transform: translateX(-140px);
  }
}
.tooltip-date {
  font-size: 10px;
  margin-bottom: 0.15em;

  i {
    display: inline-block;
    width: 1.1em;
    margin-right: 0.3em;
    text-align: center;
    color: $natural;
  }
}
.tooltip-value {
  font-size: 14px;
  font-weight: bold;
  margin-top: 0.3em;
}
</style>
